import {
  ArrowRight,
  ArrowDown,
  Brain,
  Check,
  CheckCircle2,
  Copy,
  Download,
  Eraser,
  Eye,
  File,
  FileText,
  Film,
  FolderKanban,
  Globe2,
  Image as ImageIcon,
  LoaderCircle,
  MessageCircle,
  Paperclip,
  Pencil,
  RotateCcw,
  Save,
  Send,
  ShieldCheck,
  Square,
  Timer,
  Trash2,
  UserRound,
  WandSparkles,
  Wifi,
  WifiOff,
  Wrench,
  X,
} from "lucide-react";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  attachmentRawUrl,
  deleteAttachment,
  getQuickPrompts,
  listPendingAttachments,
  rawFileUrl,
  uploadAttachment,
} from "../api";
import type { ApprovalDecision, ApprovalRecord, AttachmentRecord, EventRecord, InteractionAnswerInput, MessageRecord, Notify, QuickPromptRecord, RunRecord, SessionDetail } from "../types";
import { ApprovalPrompt } from "./ApprovalPrompt";
import { ResponsiveTable } from "./ResponsiveTable";
import { QuickPromptsManager } from "./QuickPromptsManager";
import { AttachmentManager } from "./AttachmentManager";
import { InteractionPrompt, parseInteractionRequest, type InteractionResolution } from "./InteractionPrompt";
import { NoraPresence, type NoraPresenceState } from "./NoraPresence";

const TIMELINE_BATCH_SIZE = 80;
const TIMELINE_WINDOW_SIZE = 160;
const USER_SCROLL_INTENT_MS = 750;
const FILE_ACTIVATION_MAX_DURATION_MS = 500;
const FILE_ACTIVATION_MOVE_TOLERANCE_PX = 10;
const FILE_ACTIVATION_BLOCK_AFTER_GESTURE_MS = 1_000;
const FILE_KEYBOARD_ACTIVATION_WINDOW_MS = 1_000;

interface ChatViewProps {
  detail: SessionDetail | null;
  loading: boolean;
  sending: boolean;
  workspaceRoot: string;
  runtimeAvailable: boolean;
  runtimeTitle: string;
  onSend(content: string, attachmentIds: string[]): Promise<boolean>;
  onRespond(requestId: string, answers: InteractionAnswerInput[]): Promise<boolean>;
  onResolveApproval(approvalId: string, decision: ApprovalDecision): Promise<boolean>;
  onRetry(messageId: string): Promise<void>;
  onCancel(runId: string): Promise<void>;
  onOpenMemory(): void;
  onOpenFile(path: string, line: number | null): void;
  onOpenSession(sessionId: string): void;
  onNotify: Notify;
}

export function ChatView(props: ChatViewProps) {
  if (!props.detail) {
    return (
      <section className="chat-view empty-chat-view">
        <div className="empty-chat-copy">
          {props.loading ? <LoaderCircle className="empty-chat-icon spin" aria-hidden="true" /> : <MessageCircle className="empty-chat-icon" aria-hidden="true" />}
          <strong>{props.loading ? "Chargement" : "Aucune session sélectionnée"}</strong>
        </div>
      </section>
    );
  }

  const activeRun = props.detail.runs.find((run) => ["queued", "running", "cancelling"].includes(run.status)) ?? null;
  const pendingApprovals = props.detail.approvals ?? [];
  const presenceState: NoraPresenceState = pendingApprovals.length || hasPendingInteraction(props.detail.messages, props.detail.events)
    ? "waiting"
    : props.sending || activeRun ? "working" : "idle";
  return (
    <section className="chat-view">
      <ConversationTimeline
        key={props.detail.session.id}
        detail={props.detail}
        workspaceRoot={props.workspaceRoot}
        onOpenMemory={props.onOpenMemory}
        onOpenFile={props.onOpenFile}
        onOpenSession={props.onOpenSession}
        retryDisabled={props.sending || Boolean(activeRun)}
        onRetry={props.onRetry}
        onRespond={props.onRespond}
      />
      <Composer
        key={props.detail.session.id}
        sessionId={props.detail.session.id}
        projectSession={props.detail.session.projectId != null}
        sending={props.sending}
        activeRun={activeRun}
        pendingApprovals={pendingApprovals}
        presenceState={presenceState}
        runtimeAvailable={props.runtimeAvailable}
        runtimeTitle={props.runtimeTitle}
        onSend={props.onSend}
        onCancel={props.onCancel}
        onResolveApproval={props.onResolveApproval}
        onNotify={props.onNotify}
      />
    </section>
  );
}

const ConversationTimeline = memo(function ConversationTimeline(props: {
  detail: SessionDetail;
  workspaceRoot: string;
  onOpenMemory(): void;
  onOpenFile(path: string, line: number | null): void;
  onOpenSession(sessionId: string): void;
  retryDisabled: boolean;
  onRetry(messageId: string): Promise<void>;
  onRespond(requestId: string, answers: InteractionAnswerInput[]): Promise<boolean>;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const messageColumn = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const initialScrollSettlesAt = useRef(0);
  const userScrollIntentUntil = useRef(0);
  const pointerStartY = useRef<number | null>(null);
  const [atBottom, setAtBottom] = useState(true);
  const timeline = useMemo(
    () => buildTimeline(props.detail.messages, props.detail.events),
    [props.detail.messages, props.detail.events]
  );
  const [visibleRange, setVisibleRange] = useState(() => latestTimelineRange(timeline.length));
  const pendingWindowAnchor = useRef<{ element: HTMLElement; viewportOffset: number } | null>(null);
  const pendingScrollToBottom = useRef(false);
  const previousTimelineLength = useRef(timeline.length);
  const visibleTimeline = useMemo(
    () => timeline.slice(visibleRange.start, visibleRange.end),
    [timeline, visibleRange]
  );
  const interactionResolutions = useMemo(
    () => findInteractionResolutions(props.detail.messages, props.detail.events),
    [props.detail.messages, props.detail.events]
  );
  const runsById = useMemo(
    () => new Map(props.detail.runs.map((run) => [run.id, run])),
    [props.detail.runs]
  );
  const approvalRunIds = useMemo(
    () => new Set((props.detail.approvals ?? []).map((approval) => approval.runId)),
    [props.detail.approvals]
  );
  const activityEventsByRunId = useMemo(() => {
    const eventsByRunId = new Map<string, EventRecord[]>();
    for (const event of props.detail.events) {
      if (!event.runId) continue;
      const runEvents = eventsByRunId.get(event.runId);
      if (runEvents) runEvents.push(event);
      else eventsByRunId.set(event.runId, [event]);
    }
    return eventsByRunId;
  }, [props.detail.events]);

  useEffect(() => {
    stickToBottom.current = true;
    userScrollIntentUntil.current = 0;
    pointerStartY.current = null;
    initialScrollSettlesAt.current = performance.now() + 1_000;
    setAtBottom(true);
    const frame = requestAnimationFrame(() => {
      scrollToBottom(scroller.current);
      requestAnimationFrame(() => scrollToBottom(scroller.current));
    });
    const timer = window.setTimeout(() => scrollToBottom(scroller.current), 300);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [props.detail.session.id]);

  useEffect(() => {
    if (stickToBottom.current) requestAnimationFrame(() => scrollToBottom(scroller.current));
  }, [props.detail.messages]);
  useEffect(() => {
    const added = timeline.length - previousTimelineLength.current;
    const previousLength = previousTimelineLength.current;
    previousTimelineLength.current = timeline.length;
    setVisibleRange((current) => {
      if (added > 0 && stickToBottom.current && current.end >= previousLength) {
        const width = Math.min(
          TIMELINE_WINDOW_SIZE,
          Math.max(TIMELINE_BATCH_SIZE, current.end - current.start)
        );
        const next = { start: Math.max(0, timeline.length - width), end: timeline.length };
        return sameTimelineRange(current, next) ? current : next;
      }
      if (current.end <= timeline.length) return current;
      const next = latestTimelineRange(timeline.length, Math.min(TIMELINE_WINDOW_SIZE, current.end - current.start));
      return sameTimelineRange(current, next) ? current : next;
    });
  }, [timeline.length]);
  useLayoutEffect(() => {
    const node = scroller.current;
    if (!node) return;
    if (pendingScrollToBottom.current) {
      pendingScrollToBottom.current = false;
      scrollToBottom(node, true);
      return;
    }
    const pending = pendingWindowAnchor.current;
    if (!pending) return;
    pendingWindowAnchor.current = null;
    if (!pending.element.isConnected) return;
    const nextOffset = pending.element.getBoundingClientRect().top - node.getBoundingClientRect().top;
    node.scrollTop += nextOffset - pending.viewportOffset;
  }, [visibleRange]);
  useEffect(() => {
    const node = messageColumn.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (stickToBottom.current) requestAnimationFrame(() => scrollToBottom(scroller.current));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [props.detail.session.id]);

  function onScroll() {
    const node = scroller.current;
    if (!node) return;
    const now = performance.now();
    const atWindowBottom = node.scrollHeight - node.scrollTop - node.clientHeight < 24;
    const latestVisible = visibleRange.end >= timeline.length;
    const next = atWindowBottom && latestVisible;
    const userScrollIntent = now < userScrollIntentUntil.current;
    if (now < initialScrollSettlesAt.current && stickToBottom.current && !userScrollIntent) {
      stickToBottom.current = true;
      setAtBottom(true);
      if (!next) requestAnimationFrame(() => scrollToBottom(node));
      return;
    }
    if (!next && stickToBottom.current && !userScrollIntent) {
      stickToBottom.current = true;
      setAtBottom(true);
      requestAnimationFrame(() => scrollToBottom(node));
      return;
    }
    stickToBottom.current = next;
    setAtBottom((current) => current === next ? current : next);
    if (!next && node.scrollTop < 180) revealEarlier();
    else if (atWindowBottom && !latestVisible) revealNewer();
  }

  function markUserScrollIntent() {
    userScrollIntentUntil.current = performance.now() + USER_SCROLL_INTENT_MS;
  }

  function revealEarlier() {
    const node = scroller.current;
    if (!node || visibleRange.start <= 0 || pendingScrollToBottom.current) return;
    const start = Math.max(0, visibleRange.start - TIMELINE_BATCH_SIZE);
    const end = Math.min(visibleRange.end, start + TIMELINE_WINDOW_SIZE);
    rememberWindowAnchor("first");
    stickToBottom.current = false;
    setAtBottom(false);
    setVisibleRange({ start, end });
  }

  function revealNewer() {
    if (visibleRange.end >= timeline.length || pendingScrollToBottom.current) return;
    const end = Math.min(timeline.length, visibleRange.end + TIMELINE_BATCH_SIZE);
    const start = Math.max(visibleRange.start, end - TIMELINE_WINDOW_SIZE);
    rememberWindowAnchor("last");
    stickToBottom.current = false;
    setAtBottom(false);
    setVisibleRange({ start, end });
  }

  function rememberWindowAnchor(position: "first" | "last") {
    const node = scroller.current;
    const elements = messageColumn.current?.querySelectorAll<HTMLElement>(":scope > .message, :scope > .memory-chat-event");
    if (!node || !elements?.length) return;
    const element = position === "first" ? elements[0] : elements[elements.length - 1];
    if (!element) return;
    pendingWindowAnchor.current = {
      element,
      viewportOffset: element.getBoundingClientRect().top - node.getBoundingClientRect().top,
    };
  }

  function jumpToLatest() {
    stickToBottom.current = true;
    userScrollIntentUntil.current = 0;
    setAtBottom(true);
    const next = latestTimelineRange(timeline.length);
    if (sameTimelineRange(visibleRange, next)) scrollToBottom(scroller.current, true);
    else {
      pendingWindowAnchor.current = null;
      pendingScrollToBottom.current = true;
      setVisibleRange(next);
    }
  }

  return (
    <div className="conversation-timeline">
      <div
        className="message-scroller"
        ref={scroller}
        onScroll={onScroll}
        onWheel={markUserScrollIntent}
        onTouchMove={markUserScrollIntent}
        onPointerDown={(event) => {
          if (event.pointerType === "touch" || event.pointerType === "pen") pointerStartY.current = event.clientY;
          else if (event.target === event.currentTarget) markUserScrollIntent();
        }}
        onPointerMove={(event) => {
          if (pointerStartY.current != null && Math.abs(event.clientY - pointerStartY.current) > 4) {
            markUserScrollIntent();
          }
        }}
        onPointerUp={() => { pointerStartY.current = null; }}
        onPointerCancel={() => { pointerStartY.current = null; }}
      >
        <div className="message-column" ref={messageColumn}>
          {!timeline.length ? (
            <div className="conversation-empty" aria-label="Conversation vide">
              <MessageCircle className="empty-chat-icon" aria-hidden="true" />
            </div>
          ) : <>
            {visibleRange.start > 0 ? (
              <button className="timeline-history-control older" type="button" onClick={revealEarlier}>
                <strong>Afficher les messages précédents</strong>
                <small>{visibleRange.start.toLocaleString("fr-FR")} élément{visibleRange.start > 1 ? "s" : ""} plus ancien{visibleRange.start > 1 ? "s" : ""}</small>
              </button>
            ) : null}
            {visibleTimeline.map((item) => item.kind === "message"
            ? <Message
                key={`message:${item.message.id}`}
                message={item.message}
                workspaceRoot={props.workspaceRoot}
                workspacePath={props.detail.session.workspacePath}
                onOpenFile={props.onOpenFile}
                onOpenSession={props.onOpenSession}
                retryDisabled={props.retryDisabled}
                onRetry={props.onRetry}
                onRespond={props.onRespond}
                interactionResolutions={interactionResolutions}
                run={item.message.runId ? runsById.get(item.message.runId) ?? null : null}
                waitingForApproval={Boolean(item.message.runId && approvalRunIds.has(item.message.runId))}
                activityEvents={item.message.status === "draft" && item.message.runId
                  ? activityEventsByRunId.get(item.message.runId) ?? EMPTY_EVENTS
                  : EMPTY_EVENTS}
              />
            : <MemoryEvent key={`event:${item.event.id}`} event={item.event} onOpen={props.onOpenMemory} />)}
            {visibleRange.end < timeline.length ? (
              <button className="timeline-history-control newer" type="button" onClick={revealNewer}>
                <strong>Afficher les messages suivants</strong>
                <small>{(timeline.length - visibleRange.end).toLocaleString("fr-FR")} élément{timeline.length - visibleRange.end > 1 ? "s" : ""} plus récent{timeline.length - visibleRange.end > 1 ? "s" : ""}</small>
              </button>
            ) : null}
          </>}
        </div>
      </div>
      {!atBottom ? (
        <button className="jump-to-latest" type="button" onClick={jumpToLatest} aria-label="Aller au dernier message" title="Dernier message">
          <ArrowDown aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
});

function Composer(props: {
  sessionId: string;
  projectSession: boolean;
  sending: boolean;
  activeRun: RunRecord | null;
  pendingApprovals: ApprovalRecord[];
  presenceState: NoraPresenceState;
  runtimeAvailable: boolean;
  runtimeTitle: string;
  onSend(content: string, attachmentIds: string[]): Promise<boolean>;
  onCancel(runId: string): Promise<void>;
  onResolveApproval(approvalId: string, decision: ApprovalDecision): Promise<boolean>;
  onNotify: Notify;
}) {
  const [content, setContent] = useState(() => readDraft(props.sessionId));
  const [promptsOpen, setPromptsOpen] = useState(false);
  const [promptManagerOpen, setPromptManagerOpen] = useState(false);
  const [attachmentManagerOpen, setAttachmentManagerOpen] = useState(false);
  const [prompts, setPrompts] = useState<QuickPromptRecord[]>([]);
  const [promptsLoading, setPromptsLoading] = useState(true);
  const [attachments, setAttachments] = useState<AttachmentRecord[]>([]);
  const [attachmentError, setAttachmentError] = useState("");
  const [approvalBusyId, setApprovalBusyId] = useState<string | null>(null);
  const [uploading, setUploading] = useState<{ name: string; progress: number } | null>(null);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const composerWrap = useRef<HTMLDivElement>(null);
  const draftKey = `nora.draft.${props.sessionId}`;
  const availablePrompts = useMemo(
    () => prompts.filter((prompt) => prompt.context === "all" || prompt.context === (props.projectSession ? "project" : "general")),
    [prompts, props.projectSession]
  );

  const reloadPrompts = useCallback(async () => {
    setPromptsLoading(true);
    try {
      setPrompts(await getQuickPrompts());
    } finally {
      setPromptsLoading(false);
    }
  }, []);

  useEffect(() => resizeTextarea(textarea.current), [content]);
  useEffect(() => {
    let active = true;
    void reloadPrompts().catch(() => {
      if (active) setPrompts([]);
    });
    void listPendingAttachments(props.sessionId).then((items) => {
      if (active) setAttachments(items);
    }).catch(() => {
      if (active) setAttachmentError("Les pièces jointes en attente n’ont pas pu être chargées.");
    });
    return () => { active = false; };
  }, [props.sessionId, reloadPrompts]);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (content) localStorage.setItem(draftKey, content);
      else localStorage.removeItem(draftKey);
    }, 120);
    return () => window.clearTimeout(timer);
  }, [content, draftKey]);
  useEffect(() => {
    if (!promptsOpen) return;
    const close = (event: PointerEvent) => {
      if (!composerWrap.current?.contains(event.target as Node)) setPromptsOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [promptsOpen]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const text = content.trim();
    if ((!text && !attachments.length) || uploading || props.sending || props.activeRun) return;
    setPromptsOpen(false);
    const sent = await props.onSend(text, attachments.map((attachment) => attachment.id));
    if (sent) {
      setContent("");
      setAttachments([]);
      setAttachmentError("");
      setAttachmentManagerOpen(false);
      localStorage.removeItem(draftKey);
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Escape" && promptsOpen) {
      event.preventDefault();
      setPromptsOpen(false);
      return;
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      event.currentTarget.form?.requestSubmit();
    }
  }

  function selectPrompt(prompt: string) {
    setContent((current) => current.trim() === "/" || !current.trim() ? prompt : `${current.trimEnd()}\n${prompt}`);
    setPromptsOpen(false);
    requestAnimationFrame(() => textarea.current?.focus());
  }

  function clearDraft() {
    setContent("");
    setPromptsOpen(false);
    localStorage.removeItem(draftKey);
    requestAnimationFrame(() => textarea.current?.focus());
  }

  async function selectFiles(event: ChangeEvent<HTMLInputElement>) {
    const files = [...(event.target.files ?? [])];
    event.target.value = "";
    if (!files.length) return;
    setAttachmentManagerOpen(true);
    const remaining = 5 - attachments.length;
    if (remaining <= 0) {
      setAttachmentError("Un message accepte au maximum 5 pièces jointes.");
      return;
    }
    if (files.length > remaining) {
      setAttachmentError(`Il reste ${remaining} emplacement${remaining > 1 ? "s" : ""} disponible${remaining > 1 ? "s" : ""}.`);
      return;
    }
    for (const file of files) {
      const isVideo = /\.(mp4|mov|m4v|webm|ogv)$/i.test(file.name) || (!file.name.includes(".") && file.type.startsWith("video/"));
      const limitMo = isVideo ? 100 : 25;
      if (file.size > limitMo * 1024 * 1024) {
        setAttachmentError(`${file.name} dépasse la limite de ${limitMo} Mo.`);
        return;
      }
    }
    const totalBytes = attachments.reduce((total, file) => total + file.sizeBytes, 0)
      + files.reduce((total, file) => total + file.size, 0);
    if (totalBytes > 200 * 1024 * 1024) {
      setAttachmentError("Les pièces jointes du message dépassent la limite totale de 200 Mo.");
      return;
    }
    setAttachmentError("");
    let added = 0;
    for (const file of files) {
      try {
        setUploading({ name: file.name, progress: 0 });
        const attachment = await uploadAttachment(props.sessionId, file, (progress) => {
          setUploading({ name: file.name, progress });
        });
        setAttachments((current) => [...current, attachment]);
        added += 1;
      } catch (error) {
        const message = error instanceof Error ? error.message : "Ajout de la pièce jointe impossible.";
        setAttachmentError(message);
        props.onNotify(message, "error");
        break;
      } finally {
        setUploading(null);
      }
    }
    if (added) props.onNotify(`${added} pièce${added > 1 ? "s" : ""} jointe${added > 1 ? "s" : ""} ajoutée${added > 1 ? "s" : ""}.`, "success");
  }

  async function removeAttachment(attachment: AttachmentRecord) {
    try {
      await deleteAttachment(attachment.id);
      setAttachments((current) => current.filter((item) => item.id !== attachment.id));
      setAttachmentError("");
      props.onNotify("Pièce jointe supprimée.", "success");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Suppression impossible.";
      setAttachmentError(message);
      props.onNotify(message, "error");
    }
  }

  async function resolvePendingApproval(approval: ApprovalRecord, decision: ApprovalDecision) {
    if (approvalBusyId) return;
    setApprovalBusyId(approval.id);
    try {
      await props.onResolveApproval(approval.id, decision);
    } finally {
      setApprovalBusyId(null);
    }
  }

  return (
    <div className="composer-zone">
      <div className="composer-wrap" ref={composerWrap}>
        <NoraPresence state={props.presenceState} />
        {props.pendingApprovals.length ? (
          <div className="approval-chat-stack" aria-live="polite">
            {props.pendingApprovals.map((approval) => (
              <ApprovalPrompt
                key={approval.id}
                approval={approval}
                busy={approvalBusyId === approval.id}
                context="chat"
                onDecision={(decision) => void resolvePendingApproval(approval, decision)}
              />
            ))}
          </div>
        ) : null}
        {promptsOpen ? (
          <div className="quick-prompts" role="menu" aria-label="Prompts rapides">
            <header>
              <WandSparkles aria-hidden="true" />
              <strong>Prompts rapides</strong>
              <button type="button" onClick={() => { setPromptsOpen(false); setPromptManagerOpen(true); }} aria-label="Gérer les prompts rapides" title="Gérer">
                <Pencil aria-hidden="true" />
              </button>
            </header>
            {promptsLoading ? <p><LoaderCircle className="spin" aria-hidden="true" />Chargement…</p> : availablePrompts.map((prompt) => (
              <button type="button" role="menuitem" key={prompt.id} onClick={() => selectPrompt(prompt.content)}>
                <span><strong>{prompt.title}</strong><small>{prompt.description}</small></span>
                <Check aria-hidden="true" />
              </button>
            ))}
            {!promptsLoading && !availablePrompts.length ? <p>Aucun prompt pour cette conversation.</p> : null}
          </div>
        ) : null}
        {promptManagerOpen ? (
          <QuickPromptsManager
            prompts={prompts}
            onChanged={reloadPrompts}
            onClose={() => setPromptManagerOpen(false)}
            onNotify={props.onNotify}
          />
        ) : null}
        {attachmentManagerOpen ? (
          <AttachmentManager
            attachments={attachments}
            uploading={uploading}
            error={attachmentError}
            onAdd={() => fileInput.current?.click()}
            onRemove={(attachment) => void removeAttachment(attachment)}
            onClose={() => setAttachmentManagerOpen(false)}
          />
        ) : null}
        <form className="composer" onSubmit={submit}>
          <textarea
            ref={textarea}
            value={content}
            onChange={(event) => {
              setContent(event.target.value);
              if (event.target.value === "/") setPromptsOpen(true);
            }}
            onKeyDown={onKeyDown}
            placeholder="Écrire à Nora"
            rows={1}
            disabled={props.sending || props.activeRun?.status === "cancelling"}
            aria-label="Message"
          />
          <div className="composer-footer">
            <div className="composer-leading">
              <span
                className={`composer-runtime ${props.runtimeAvailable ? "healthy" : "warning"}`}
                role="status"
                aria-label={props.runtimeTitle}
                title={props.runtimeTitle}
              >
                {props.runtimeAvailable ? <Wifi aria-hidden="true" /> : <WifiOff aria-hidden="true" />}
              </span>
              {attachments.length || uploading || attachmentError || content || props.activeRun ? (
                <button
                  className={`composer-state ${attachmentError ? "error" : ""} ${attachments.length || uploading || attachmentError ? "actionable" : ""}`}
                  type="button"
                  onClick={() => {
                    if (!attachments.length && !uploading && !attachmentError) return;
                    setPromptsOpen(false);
                    setAttachmentManagerOpen(true);
                  }}
                  title={attachments.length ? "Gérer les pièces jointes" : undefined}
                >
                  {composerStateLabel(props.activeRun, props.pendingApprovals.length > 0, content, attachments.length, uploading, attachmentError)}
                </button>
              ) : <span className="composer-state" aria-hidden="true" />}
            </div>
            <div className="composer-actions">
              <button
                className="composer-tool"
                type="button"
                onClick={clearDraft}
                disabled={!content}
                aria-label="Effacer le brouillon"
                title="Effacer le brouillon"
              >
                <Eraser aria-hidden="true" />
              </button>
              <button
                className={`composer-tool ${promptsOpen ? "active" : ""}`}
                type="button"
                onClick={() => setPromptsOpen((current) => !current)}
                aria-label="Ouvrir les prompts rapides"
                aria-expanded={promptsOpen}
                title="Prompts rapides"
              >
                <WandSparkles aria-hidden="true" />
              </button>
              <input
                ref={fileInput}
                className="composer-file-input"
                type="file"
                multiple
                accept="image/png,image/jpeg,image/webp,image/gif,image/avif,image/heic,image/heif,video/mp4,video/quicktime,video/x-m4v,video/webm,video/ogg,.mp4,.mov,.m4v,.webm,.ogv,application/pdf,text/plain,text/markdown,text/csv,application/json,application/xml,.jsonl,.yaml,.yml,.docx,.xlsx,.pptx,.odt,.ods,.odp"
                onChange={selectFiles}
                tabIndex={-1}
                aria-label="Sélectionner des fichiers"
              />
              <button
                className={`composer-tool attachment-tool ${attachments.length ? "has-attachments" : ""}`}
                type="button"
                onClick={() => { setPromptsOpen(false); setAttachmentManagerOpen(true); }}
                disabled={Boolean(uploading) || props.sending || Boolean(props.activeRun)}
                aria-label={attachments.length ? "Gérer les pièces jointes" : "Ajouter une pièce jointe"}
                title={attachments.length ? "Pièces jointes" : "Ajouter une pièce jointe"}
              >
                <Paperclip aria-hidden="true" />
                {attachments.length ? <span className="attachment-count">{attachments.length}</span> : null}
              </button>
              {props.activeRun ? (
                <button
                  className="composer-action stop"
                  type="button"
                  onClick={() => props.onCancel(props.activeRun!.id)}
                  disabled={props.activeRun.status === "cancelling"}
                  aria-label="Interrompre la réponse"
                  title="Interrompre"
                >
                  <Square aria-hidden="true" />
                </button>
              ) : (
                <button className="composer-action send" type="submit" disabled={props.sending || Boolean(uploading) || (!content.trim() && !attachments.length)} aria-label="Envoyer" title="Envoyer">
                  <Send aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

const MemoryEvent = memo(function MemoryEvent({ event, onOpen }: { event: EventRecord; onOpen(): void }) {
  const title = typeof event.payload.title === "string" ? event.payload.title : "Souvenir durable";
  const content = typeof event.payload.content === "string" ? event.payload.content : "";
  const fromNora = event.payload.createdBy === "nora";
  const meta = event.type === "memory.forgotten"
    ? "Souvenir oublié"
    : event.type === "memory.updated"
      ? "Souvenir corrigé"
      : fromNora ? "Nora a mémorisé" : "Mémoire ajoutée";
  const Icon = event.type === "memory.forgotten" ? Trash2 : event.type === "memory.updated" ? Pencil : Save;
  return (
    <button className={`memory-chat-event ${event.type.split(".")[1] ?? "created"}`} type="button" onClick={onOpen}>
      <span className="memory-chat-icon"><Icon aria-hidden="true" /></span>
      <span className="memory-chat-copy">
        <span><strong>{meta}</strong><time>{formatMessageTime(event.createdAt)}</time></span>
        <b>{title}</b>
        {content ? <small>{content}</small> : null}
      </span>
    </button>
  );
}, (previous, next) => previous.event.id === next.event.id && previous.event.type === next.event.type && previous.event.payload === next.event.payload);

const Message = memo(function Message(props: {
  message: MessageRecord;
  workspaceRoot: string;
  workspacePath: string;
  onOpenFile(path: string, line: number | null): void;
  onOpenSession(sessionId: string): void;
  retryDisabled: boolean;
  onRetry(messageId: string): Promise<void>;
  onRespond(requestId: string, answers: InteractionAnswerInput[]): Promise<boolean>;
  interactionResolutions: ReadonlyMap<string, InteractionResolution>;
  run: RunRecord | null;
  waitingForApproval: boolean;
  activityEvents: EventRecord[];
}) {
  const [copied, setCopied] = useState(false);
  const copyResetTimer = useRef<number | null>(null);
  const attachments = props.message.attachments ?? [];
  const displayContent = stripInteractionMarkers(props.message.content);
  const responseDuration = getResponseDurationMs(props.message, props.run);
  const responseDurationLabel = responseDuration == null ? null : formatResponseDuration(responseDuration);

  useEffect(() => () => {
    if (copyResetTimer.current != null) window.clearTimeout(copyResetTimer.current);
  }, []);

  async function copy() {
    const copyValue = stripInteractionMetadata(props.message.content) || attachments.map((attachment) => attachment.originalName).join("\n");
    await writeClipboard(copyValue);
    setCopied(true);
    if (copyResetTimer.current != null) window.clearTimeout(copyResetTimer.current);
    copyResetTimer.current = window.setTimeout(() => {
      copyResetTimer.current = null;
      setCopied(false);
    }, 1200);
  }

  return (
    <article className={`message ${props.message.role} ${props.message.status}`}>
      <div className="message-content">
        {props.message.role === "user" ? (
          <div className="user-message-header">
            <span className="user-message-author"><UserRound aria-hidden="true" /><strong>Vous</strong></span>
            <time dateTime={props.message.createdAt}>{formatMessageDate(props.message.createdAt)}</time>
            <span className="user-message-actions">
              <button type="button" onClick={copy} aria-label="Copier ce message" title={copied ? "Copié" : "Copier"}>
                {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
              </button>
              <button
                type="button"
                onClick={() => void props.onRetry(props.message.id)}
                disabled={props.retryDisabled}
                aria-label="Relancer ce message"
                title="Relancer"
              >
                <RotateCcw aria-hidden="true" />
              </button>
            </span>
          </div>
        ) : (
          <div className="message-meta">
            <span className="assistant-message-author"><span className="nora-logo" aria-hidden="true" /><strong>Nora</strong></span>
            <time dateTime={props.message.createdAt}>{formatMessageDate(props.message.createdAt)}</time>
            {props.message.content ? (
              <button className="message-copy" type="button" onClick={copy} aria-label="Copier le message" title={copied ? "Copié" : "Copier"}>
                {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
              </button>
            ) : null}
          </div>
        )}
        {props.message.role === "assistant" && props.message.status === "draft" ? (
          <ResponseActivity run={props.run} events={props.activityEvents} waitingForApproval={props.waitingForApproval} />
        ) : null}
        {attachments.length ? (
          <MessageAttachments attachments={attachments} onOpenFile={props.onOpenFile} />
        ) : null}
        {displayContent ? (
          <RichMarkdown
            content={displayContent}
            workspaceRoot={props.workspaceRoot}
            workspacePath={props.workspacePath}
            onOpenFile={props.onOpenFile}
            interactions={props.message.role === "assistant" && props.message.status === "final" ? {
              resolutions: props.interactionResolutions,
              disabled: props.retryDisabled,
              onSubmit: props.onRespond,
            } : null}
          />
        ) : null}
        {props.message.role === "user" && props.message.routedTo ? (
          <button
            className="project-route-link"
            type="button"
            onClick={() => props.onOpenSession(props.message.routedTo!.sessionId)}
            aria-label={`Ouvrir la suite dans le projet ${props.message.routedTo.projectName}`}
          >
            <span className="project-route-icon"><FolderKanban aria-hidden="true" /></span>
            <span className="project-route-copy">
              <small>Suite dans le projet</small>
              <strong>{props.message.routedTo.projectName}</strong>
              <em>{props.message.routedTo.sessionTitle}</em>
            </span>
            <ArrowRight aria-hidden="true" />
          </button>
        ) : null}
        {responseDurationLabel ? (
          <footer
            className="message-response-time"
            aria-label={`Réponse affichée en ${responseDurationLabel}`}
            title="Temps total entre l’envoi et la finalisation"
          >
            <Timer aria-hidden="true" />
            <span>Réponse en {responseDurationLabel}</span>
          </footer>
        ) : null}
      </div>
    </article>
  );
}, (previous, next) => (
  previous.message.id === next.message.id
  && previous.message.content === next.message.content
  && previous.message.status === next.message.status
  && previous.message.updatedAt === next.message.updatedAt
  && previous.message.attachments === next.message.attachments
  && previous.message.routedTo?.sessionId === next.message.routedTo?.sessionId
  && previous.message.routedTo?.sessionTitle === next.message.routedTo?.sessionTitle
  && previous.message.routedTo?.projectName === next.message.routedTo?.projectName
  && previous.workspaceRoot === next.workspaceRoot
  && previous.workspacePath === next.workspacePath
  && previous.retryDisabled === next.retryDisabled
  && previous.run?.status === next.run?.status
  && previous.run?.createdAt === next.run?.createdAt
  && previous.run?.completedAt === next.run?.completedAt
  && previous.run?.operationCount === next.run?.operationCount
  && previous.run?.usedTokens === next.run?.usedTokens
  && previous.waitingForApproval === next.waitingForApproval
  && previous.activityEvents === next.activityEvents
  && sameStringMap(previous.interactionResolutions, next.interactionResolutions)
));

const EMPTY_EVENTS: EventRecord[] = [];

interface ResponseActivityItem {
  key: string;
  kind: string;
  label: string;
  status: "active" | "completed" | "warning";
  order: number;
}

function ResponseActivity({ run, events, waitingForApproval }: { run: RunRecord | null; events: EventRecord[]; waitingForApproval: boolean }) {
  const items = useMemo(() => buildResponseActivity(events), [events]);
  const operationCount = Math.max(run?.operationCount ?? 0, countResponseOperations(events));
  const usedTokens = Math.max(0, run?.usedTokens ?? 0);
  const title = waitingForApproval
    ? "Validation requise"
    : run?.status === "queued"
    ? "Nora prépare la réponse"
    : run?.status === "cancelling" ? "Arrêt en cours" : "Nora travaille";
  const countersLabel = `${operationCount.toLocaleString("fr-FR")} opération${operationCount > 1 ? "s" : ""}`
    + (usedTokens > 0 ? `, ${usedTokens.toLocaleString("fr-FR")} tokens utilisés` : "");
  return (
    <section className={`response-activity ${waitingForApproval ? "waiting" : ""}`} role="status" aria-live="polite" aria-label={title}>
      <header>
        <span className="response-activity-heading">
          {waitingForApproval ? <ShieldCheck aria-hidden="true" /> : <LoaderCircle className="spin" aria-hidden="true" />}
          <strong>{title}</strong>
        </span>
        <span className="response-activity-counters" aria-label={countersLabel}>
          <span>{operationCount.toLocaleString("fr-FR")} op.</span>
          {usedTokens > 0 ? <span>{formatActivityTokens(usedTokens)} tokens</span> : null}
        </span>
      </header>
      <div className="response-activity-list">
        {items.length ? items.map((item) => (
          <div className={`response-activity-row ${item.kind} ${item.status}`} key={item.key}>
            <span>{activityIcon(item)}</span>
            <p><strong>{item.label}</strong></p>
          </div>
        )) : (
          <div className="response-activity-row active">
            <span><LoaderCircle className="spin" aria-hidden="true" /></span>
            <p><strong>Préparation du contexte</strong></p>
          </div>
        )}
      </div>
    </section>
  );
}

function buildResponseActivity(events: EventRecord[]): ResponseActivityItem[] {
  const commentaryItems = new Map<string, ResponseActivityItem>();
  let phaseItem: ResponseActivityItem | null = null;
  let connectionItem: ResponseActivityItem | null = null;
  for (const event of events) {
    if (event.type === "driver.warning" || event.type === "driver.disconnected") {
      connectionItem = {
        key: "connection",
        kind: "warning",
        label: "Connexion momentanément ralentie",
        status: "warning",
        order: event.seq,
      };
      continue;
    }
    if (event.type === "driver.reconnected") {
      connectionItem = {
        key: "connection",
        kind: "connection",
        label: "Connexion rétablie",
        status: "completed",
        order: event.seq,
      };
      continue;
    }
    if (event.type !== "driver.item.started" && event.type !== "driver.item.completed") continue;
    const itemType = typeof event.payload.type === "string" ? event.payload.type : "";
    const itemId = typeof event.payload.id === "string" ? event.payload.id : event.id;
    if (itemType === "agentMessage") {
      const commentary = event.payload.phase === "commentary" && typeof event.payload.text === "string"
        ? compactText(event.payload.text, 280)
        : "";
      if (commentary) {
        commentaryItems.set(itemId, {
          key: itemId,
          kind: "commentary",
          label: commentary,
          status: "completed",
          order: event.seq,
        });
      }
      continue;
    }
    const descriptor = activityDescriptor(itemType);
    if (!descriptor) continue;
    const failed = event.payload.status === "failed"
      || (typeof event.payload.exitCode === "number" && event.payload.exitCode !== 0);
    phaseItem = {
      key: "current-phase",
      kind: descriptor.kind,
      label: descriptor.label,
      status: failed ? "warning" : event.type === "driver.item.completed" ? "completed" : "active",
      order: event.seq,
    };
  }

  return [
    ...[...commentaryItems.values()].slice(-2),
    phaseItem,
    connectionItem,
  ]
    .filter((item): item is ResponseActivityItem => item != null)
    .sort((left, right) => left.order - right.order)
    .slice(-3);
}

function activityDescriptor(type: string): Pick<ResponseActivityItem, "kind" | "label"> | null {
  if (type === "reasoning") return { kind: "reasoning", label: "Analyse de la demande" };
  if (type === "webSearch") return { kind: "web", label: "Recherche d’informations" };
  if (type === "commandExecution") return { kind: "command", label: "Vérifications en cours" };
  if (["dynamicToolCall", "mcpToolCall"].includes(type)) return { kind: "tool", label: "Action en cours" };
  if (["collabAgentToolCall", "collabToolCall"].includes(type)) return { kind: "agent", label: "Analyse complémentaire" };
  if (type === "imageView") return { kind: "image", label: "Analyse de l’image" };
  if (type === "imageGeneration") return { kind: "image", label: "Création de l’image" };
  if (type === "fileChange") return { kind: "file", label: "Mise à jour des fichiers" };
  if (type === "contextCompaction") return { kind: "context", label: "Organisation du contexte" };
  return null;
}

function activityIcon(item: ResponseActivityItem) {
  if (item.status === "active") return <LoaderCircle className="spin" aria-hidden="true" />;
  if (item.status === "warning") return <WifiOff aria-hidden="true" />;
  if (item.kind === "reasoning" || item.kind === "context" || item.kind === "commentary") return <Brain aria-hidden="true" />;
  if (item.kind === "web") return <Globe2 aria-hidden="true" />;
  if (item.kind === "command" || item.kind === "tool" || item.kind === "agent") return <Wrench aria-hidden="true" />;
  if (item.kind === "image") return <ImageIcon aria-hidden="true" />;
  if (item.kind === "file") return <Pencil aria-hidden="true" />;
  return <CheckCircle2 aria-hidden="true" />;
}

function compactText(value: string, maxLength: number): string {
  const normalized = value.replace(/\s+/g, " ").trim();
  return normalized.length > maxLength ? `${normalized.slice(0, maxLength - 1)}…` : normalized;
}

function countResponseOperations(events: EventRecord[]): number {
  const operationIds = new Set<string>();
  for (const event of events) {
    if (event.type !== "driver.item.started") continue;
    const type = typeof event.payload.type === "string" ? event.payload.type : "";
    if (!type || type === "userMessage" || type === "agentMessage") continue;
    operationIds.add(typeof event.payload.id === "string" ? event.payload.id : event.id);
  }
  return operationIds.size;
}

function formatActivityTokens(value: number): string {
  if (value >= 1_000_000) return `${compactNumberFormatter.format(value / 1_000_000)} M`;
  if (value >= 1_000) return `${compactNumberFormatter.format(value / 1_000)} k`;
  return value.toLocaleString("fr-FR");
}

function MessageAttachments(props: {
  attachments: AttachmentRecord[];
  onOpenFile(path: string, line: number | null): void;
}) {
  const images = props.attachments.filter(canPreviewAttachmentImage);
  const documents = props.attachments.filter((attachment) => !canPreviewAttachmentImage(attachment));
  return (
    <div className="message-attachments">
      {images.length ? (
        <div className={`message-attachment-images count-${Math.min(images.length, 4)}`}>
          {images.map((attachment) => (
            <FileOpenButton key={attachment.id} onActivate={() => props.onOpenFile(attachment.browserPath, null)} aria-label={`Ouvrir ${attachment.originalName}`}>
              <img src={attachmentRawUrl(attachment.id)} alt={attachment.originalName} loading="lazy" />
              <span><ImageIcon aria-hidden="true" />{attachment.originalName}</span>
            </FileOpenButton>
          ))}
        </div>
      ) : null}
      {documents.length ? (
        <div className="message-attachment-documents">
          {documents.map((attachment) => (
            <FileOpenButton key={attachment.id} onActivate={() => props.onOpenFile(attachment.browserPath, null)}>
              {attachment.kind === "video" ? <Film aria-hidden="true" /> : attachment.kind === "image" ? <ImageIcon aria-hidden="true" /> : attachment.kind === "pdf" ? <FileText aria-hidden="true" /> : <File aria-hidden="true" />}
              <span><strong>{attachment.originalName}</strong><small>{attachment.kind === "video" ? "Vidéo · " : ""}{formatBytes(attachment.sizeBytes)}</small></span>
            </FileOpenButton>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function RichMarkdown(props: {
  content: string;
  workspaceRoot: string;
  workspacePath: string;
  onOpenFile(path: string, line: number | null): void;
  interactions: {
    resolutions: ReadonlyMap<string, InteractionResolution>;
    disabled: boolean;
    onSubmit(requestId: string, answers: InteractionAnswerInput[]): Promise<boolean>;
  } | null;
}) {
  const resolve = (href: string | undefined) => resolveLocalFile(href, props.workspaceRoot, props.workspacePath);
  return (
    <div className="markdown-body">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => {
            const local = resolve(href);
            if (!local) return <a href={href} target="_blank" rel="noreferrer noopener">{children}</a>;
            if (isImagePath(local.path)) {
              return (
                <MarkdownImage
                  src={rawFileUrl(local.path)}
                  downloadHref={rawFileUrl(local.path, true)}
                  downloadName={imageNameFromSource(local.path)}
                  alt={plainText(children) || imageNameFromSource(local.path)}
                  onOpen={() => props.onOpenFile(local.path, local.line)}
                />
              );
            }
            return <FileOpenButton className="markdown-file-link" onActivate={() => props.onOpenFile(local.path, local.line)}><FileText aria-hidden="true" /><span>{children}</span></FileOpenButton>;
          },
          img: ({ src, alt }) => {
            const local = resolve(src);
            if (local) {
              return (
                <MarkdownImage
                  src={rawFileUrl(local.path)}
                  downloadHref={rawFileUrl(local.path, true)}
                  downloadName={imageNameFromSource(local.path)}
                  alt={alt || imageNameFromSource(local.path)}
                  onOpen={() => props.onOpenFile(local.path, local.line)}
                />
              );
            }
            return src ? (
              <MarkdownImage
                src={src}
                downloadHref={src}
                alt={alt || imageNameFromSource(src)}
                external
              />
            ) : null;
          },
          table: ({ children }) => <ResponsiveTable>{children}</ResponsiveTable>,
          pre: ({ children }) => (
            <CodeBlock interactions={props.interactions}>{children}</CodeBlock>
          ),
        }}
      >
        {props.content}
      </ReactMarkdown>
    </div>
  );
}

function MarkdownImage(props: {
  src: string;
  downloadHref: string;
  downloadName?: string;
  alt: string;
  external?: boolean;
  onOpen?(): void;
}) {
  const [open, setOpen] = useState(true);
  const label = props.alt.trim() || imageNameFromSource(props.src);
  const downloadName = props.downloadName || imageNameFromSource(props.src);
  return (
    <figure className={`markdown-image-shell ${open ? "open" : "collapsed"}`}>
      <figcaption className="markdown-image-toolbar">
        <span><ImageIcon aria-hidden="true" /><strong>{label}</strong></span>
        <span>
          <a
            href={props.downloadHref}
            download={downloadName}
            target={props.external ? "_blank" : undefined}
            rel={props.external ? "noreferrer noopener" : undefined}
            aria-label={`Télécharger ${label}`}
            title="Télécharger"
          >
            <Download aria-hidden="true" />
          </a>
          <button
            type="button"
            onClick={() => setOpen((current) => !current)}
            aria-label={open ? `Masquer ${label}` : `Afficher ${label}`}
            title={open ? "Masquer l’image" : "Afficher l’image"}
          >
            {open ? <X aria-hidden="true" /> : <Eye aria-hidden="true" />}
          </button>
        </span>
      </figcaption>
      {open ? (
        props.onOpen ? (
          <FileOpenButton className="markdown-image-preview" onActivate={props.onOpen} aria-label={`Ouvrir ${label} dans les fichiers`}>
            <img src={props.src} alt={label} loading="lazy" />
          </FileOpenButton>
        ) : (
          <a className="markdown-image-preview external" href={props.src} target="_blank" rel="noreferrer noopener" aria-label={`Ouvrir ${label}`}>
            <img src={props.src} alt={label} loading="lazy" />
          </a>
        )
      ) : (
        <button className="markdown-image-reopen" type="button" onClick={() => setOpen(true)}>
          <Eye aria-hidden="true" />
          <span>Afficher l’image</span>
        </button>
      )}
    </figure>
  );
}

interface FilePointerActivation {
  pointerId: number;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  startedAt: number;
  releasedAt: number | null;
  valid: boolean;
}

function FileOpenButton({
  onActivate,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  onContextMenu,
  onKeyDown,
  ...props
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onClick" | "type"> & {
  onActivate(): void;
}) {
  const pointerActivation = useRef<FilePointerActivation | null>(null);
  const keyboardActivationAt = useRef<number | null>(null);
  const blockedUntil = useRef(0);

  function invalidatePointer(blockDelayedClick = false) {
    if (pointerActivation.current) pointerActivation.current.valid = false;
    if (blockDelayedClick) blockedUntil.current = performance.now() + FILE_ACTIVATION_BLOCK_AFTER_GESTURE_MS;
  }

  function handlePointerDown(event: ReactPointerEvent<HTMLButtonElement>) {
    onPointerDown?.(event);
    keyboardActivationAt.current = null;
    if (event.defaultPrevented || !event.isPrimary || event.button !== 0) {
      pointerActivation.current = null;
      return;
    }
    blockedUntil.current = 0;
    pointerActivation.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      endX: event.clientX,
      endY: event.clientY,
      startedAt: performance.now(),
      releasedAt: null,
      valid: true,
    };
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLButtonElement>) {
    onPointerMove?.(event);
    const current = pointerActivation.current;
    if (!current || current.pointerId !== event.pointerId) return;
    current.endX = event.clientX;
    current.endY = event.clientY;
    if (
      Math.abs(event.clientX - current.startX) > FILE_ACTIVATION_MOVE_TOLERANCE_PX
      || Math.abs(event.clientY - current.startY) > FILE_ACTIVATION_MOVE_TOLERANCE_PX
    ) invalidatePointer(true);
  }

  function handlePointerUp(event: ReactPointerEvent<HTMLButtonElement>) {
    onPointerUp?.(event);
    const current = pointerActivation.current;
    if (!current || current.pointerId !== event.pointerId) return;
    current.endX = event.clientX;
    current.endY = event.clientY;
    current.releasedAt = performance.now();
    if (
      Math.abs(event.clientX - current.startX) > FILE_ACTIVATION_MOVE_TOLERANCE_PX
      || Math.abs(event.clientY - current.startY) > FILE_ACTIVATION_MOVE_TOLERANCE_PX
      || current.releasedAt - current.startedAt > FILE_ACTIVATION_MAX_DURATION_MS
    ) invalidatePointer(true);
  }

  function handlePointerCancel(event: ReactPointerEvent<HTMLButtonElement>) {
    onPointerCancel?.(event);
    invalidatePointer(true);
  }

  function handleContextMenu(event: ReactMouseEvent<HTMLButtonElement>) {
    onContextMenu?.(event);
    invalidatePointer(true);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    onKeyDown?.(event);
    if (!event.defaultPrevented && !event.repeat && (event.key === "Enter" || event.key === " ")) {
      blockedUntil.current = 0;
      pointerActivation.current = null;
      keyboardActivationAt.current = performance.now();
    }
  }

  function rejectClick(event: ReactMouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
  }

  function handleClick(event: ReactMouseEvent<HTMLButtonElement>) {
    const now = performance.now();
    const pointer = pointerActivation.current;
    const keyboardAt = keyboardActivationAt.current;
    pointerActivation.current = null;
    keyboardActivationAt.current = null;

    const selection = window.getSelection();
    if (now < blockedUntil.current || (selection && !selection.isCollapsed)) {
      rejectClick(event);
      return;
    }

    const keyboardActivation = Boolean(
      event.detail === 0
      && keyboardAt != null
      && now - keyboardAt <= FILE_KEYBOARD_ACTIVATION_WINDOW_MS
    );
    const assistiveActivation = Boolean(
      event.detail === 0
      && event.clientX === 0
      && event.clientY === 0
      && event.currentTarget === document.activeElement
    );
    const hit = pointer?.releasedAt != null
      ? document.elementFromPoint(pointer.endX, pointer.endY)
      : null;
    const deliberatePointerActivation = Boolean(
      pointer?.valid
      && pointer.releasedAt != null
      && pointer.releasedAt - pointer.startedAt <= FILE_ACTIVATION_MAX_DURATION_MS
      && hit
      && (hit === event.currentTarget || event.currentTarget.contains(hit))
    );

    if (!keyboardActivation && !assistiveActivation && !deliberatePointerActivation) {
      rejectClick(event);
      return;
    }
    onActivate();
  }

  return (
    <button
      {...props}
      type="button"
      data-file-open-control="true"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onContextMenu={handleContextMenu}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
    />
  );
}

function CodeBlock({
  children,
  interactions,
}: {
  children: ReactNode;
  interactions: {
    resolutions: ReadonlyMap<string, InteractionResolution>;
    disabled: boolean;
    onSubmit(requestId: string, answers: InteractionAnswerInput[]): Promise<boolean>;
  } | null;
}) {
  const [copied, setCopied] = useState(false);
  const text = plainText(children).replace(/\n$/, "");
  const language = codeLanguage(children);
  const interaction = language === "nora-ui" && interactions ? parseInteractionRequest(text) : null;
  if (interaction && interactions) {
    return (
      <InteractionPrompt
        request={interaction}
        resolution={interactions.resolutions.get(interaction.requestId) ?? null}
        disabled={interactions.disabled}
        onSubmit={interactions.onSubmit}
      />
    );
  }
  async function copy() {
    await writeClipboard(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  }
  return (
    <div className="markdown-code-block">
      <header><span>{language || "Code"}</span><button type="button" onClick={copy} aria-label="Copier le bloc de code" title={copied ? "Copié" : "Copier"}>{copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}</button></header>
      <pre>{children}</pre>
    </div>
  );
}

function resolveLocalFile(
  href: string | undefined,
  workspaceRoot: string,
  workspacePath: string
): { path: string; line: number | null } | null {
  if (!href) return null;
  let decoded: string;
  try { decoded = decodeURIComponent(href); } catch { decoded = href; }
  if (/^(?:https?:|mailto:|tel:|data:|blob:)/i.test(decoded)) return null;

  let value = decoded;
  let line: number | null = null;
  const hashLine = value.match(/#L(\d+)(?:C\d+)?$/i);
  if (hashLine) {
    line = Number(hashLine[1]);
    value = value.slice(0, hashLine.index);
  } else {
    value = value.split("#", 1)[0] ?? value;
    const suffix = value.match(/:(\d+)(?::\d+)?$/);
    if (suffix) {
      line = Number(suffix[1]);
      value = value.slice(0, suffix.index);
    }
  }
  value = value.split("?", 1)[0] ?? value;
  const root = workspaceRoot.replace(/\/+$/, "");
  const workspace = workspacePath.replace(/\/+$/, "");
  let relative: string;
  if (value === root) relative = "";
  else if (value.startsWith(`${root}/`)) relative = value.slice(root.length + 1);
  else if (value.startsWith("/")) return null;
  else {
    const base = workspace === root ? "" : workspace.startsWith(`${root}/`) ? workspace.slice(root.length + 1) : "";
    relative = [base, value].filter(Boolean).join("/");
  }
  const normalized = normalizeRelativePath(relative);
  return normalized == null ? null : { path: normalized, line };
}

function normalizeRelativePath(value: string): string | null {
  const result: string[] = [];
  for (const segment of value.replaceAll("\\", "/").split("/")) {
    if (!segment || segment === ".") continue;
    if (segment === "..") {
      if (!result.length) return null;
      result.pop();
    } else result.push(segment);
  }
  return result.join("/");
}

function isImagePath(path: string): boolean {
  return /\.(png|jpe?g|gif|webp|avif|bmp|ico)$/i.test(path);
}

function imageNameFromSource(source: string): string {
  if (!source || /^(?:data|blob):/i.test(source)) return "image";
  let pathname = source;
  try { pathname = new URL(source, window.location.origin).pathname; } catch { /* Keep the original source. */ }
  const encodedName = pathname.replace(/\/+$/, "").split("/").at(-1) || "image";
  try { return decodeURIComponent(encodedName); } catch { return encodedName; }
}

function plainText(value: ReactNode): string {
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (Array.isArray(value)) return value.map(plainText).join("");
  if (value && typeof value === "object" && "props" in value) {
    const props = (value as { props?: { children?: ReactNode } }).props;
    return plainText(props?.children);
  }
  return "";
}

async function writeClipboard(value: string): Promise<void> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }
  } catch {
    // Some mobile WebViews expose Clipboard but reject it outside HTTPS.
  }
  const helper = document.createElement("textarea");
  helper.value = value;
  helper.setAttribute("readonly", "");
  helper.style.position = "fixed";
  helper.style.opacity = "0";
  document.body.append(helper);
  helper.select();
  document.execCommand("copy");
  helper.remove();
}

function codeLanguage(value: ReactNode): string {
  if (!value || typeof value !== "object" || !("props" in value)) return "";
  const className = (value as { props?: { className?: string } }).props?.className ?? "";
  return className.match(/language-([^\s]+)/)?.[1] ?? "";
}

function findInteractionResolutions(messages: MessageRecord[], events: EventRecord[]): ReadonlyMap<string, InteractionResolution> {
  const result = new Map<string, InteractionResolution>();
  for (const event of events) {
    if (event.type === "interaction.responded" && typeof event.payload.requestId === "string") {
      result.set(event.payload.requestId, "submitted");
    }
  }
  for (const message of messages) {
    if (message.role !== "user") continue;
    for (const match of message.content.matchAll(/<!--\s*nora-interaction-response:([A-Za-z0-9_-]{1,80})\s*-->/g)) {
      if (match[1]) result.set(match[1], "submitted");
    }
  }

  let hasLaterFreeReply = false;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (!message) continue;
    if (message.role === "user") {
      if (!/<!--\s*nora-interaction-response:[A-Za-z0-9_-]{1,80}\s*-->/i.test(message.content)) {
        hasLaterFreeReply = true;
      }
      continue;
    }
    if (!hasLaterFreeReply || message.role !== "assistant" || message.status !== "final") continue;
    for (const requestId of interactionRequestIds(message.content)) {
      if (!result.has(requestId)) result.set(requestId, "continued");
    }
  }
  return result;
}

function hasPendingInteraction(messages: MessageRecord[], events: EventRecord[]): boolean {
  const resolutions = findInteractionResolutions(messages, events);
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (!message || message.role !== "assistant" || message.status !== "final") continue;
    for (const requestId of interactionRequestIds(message.content)) {
      if (!resolutions.has(requestId)) return true;
    }
  }
  return false;
}

function interactionRequestIds(value: string): string[] {
  const result: string[] = [];
  for (const match of value.matchAll(/```nora-ui\s*([\s\S]*?)```/gi)) {
    const request = parseInteractionRequest(match[1]?.trim() ?? "");
    if (request) result.push(request.requestId);
  }
  return result;
}

function stripInteractionMarkers(value: string): string {
  return value
    .replace(/<!--\s*nora-interaction(?:-response)?:[A-Za-z0-9_-]{1,80}\s*-->/gi, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function stripInteractionMetadata(value: string): string {
  return value
    .replace(/<!--\s*nora-interaction:[A-Za-z0-9_-]{1,80}\s*-->\s*```nora-ui\s*[\s\S]*?```/gi, "")
    .replace(/```nora-ui\s*[\s\S]*?```/gi, "")
    .replace(/<!--\s*nora-interaction-response:[A-Za-z0-9_-]{1,80}\s*-->/gi, "")
    .trim();
}

function sameStringMap<T>(left: ReadonlyMap<string, T>, right: ReadonlyMap<string, T>): boolean {
  return left === right || (left.size === right.size && [...left].every(([key, value]) => right.get(key) === value));
}

function readDraft(sessionId: string): string {
  return localStorage.getItem(`nora.draft.${sessionId}`) ?? "";
}

function resizeTextarea(node: HTMLTextAreaElement | null) {
  if (!node) return;
  node.style.height = "0px";
  node.style.height = `${Math.min(160, Math.max(48, node.scrollHeight))}px`;
}

function scrollToBottom(node: HTMLDivElement | null, smooth = false) {
  node?.scrollTo({ top: node.scrollHeight, behavior: smooth ? "smooth" : "auto" });
}

function runStatus(run: RunRecord): string {
  if (run.status === "queued") return "En attente du worker";
  if (run.status === "cancelling") return "Arrêt en cours";
  return `Réponse en cours · tentative ${Math.max(1, run.attempt)}`;
}

function composerStateLabel(
  activeRun: RunRecord | null,
  waitingForApproval: boolean,
  content: string,
  attachmentCount: number,
  uploading: { name: string; progress: number } | null,
  attachmentError: string
): string {
  if (attachmentError) return "Erreur fichier";
  if (uploading) return `Upload ${uploading.progress}%`;
  if (attachmentCount) return `${attachmentCount} pièce${attachmentCount > 1 ? "s" : ""}`;
  if (waitingForApproval) return "Validation requise";
  if (activeRun) return runStatus(activeRun);
  if (content) return "Brouillon";
  return "";
}

const messageTimeFormatter = new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit" });
const messageDateFormatter = new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

function formatMessageTime(value: string): string {
  return messageTimeFormatter.format(new Date(value));
}

function formatMessageDate(value: string): string {
  return messageDateFormatter.format(new Date(value)).replace(",", " ·");
}

function getResponseDurationMs(message: MessageRecord, run: RunRecord | null): number | null {
  if (message.role !== "assistant" || message.status !== "final" || run?.status !== "completed" || !run.completedAt) return null;
  const startedAt = Date.parse(run.createdAt);
  const completedAt = Date.parse(run.completedAt);
  if (!Number.isFinite(startedAt) || !Number.isFinite(completedAt) || completedAt < startedAt) return null;
  return completedAt - startedAt;
}

const compactNumberFormatter = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 });

function formatResponseDuration(durationMs: number): string {
  if (durationMs < 100) return "moins de 0,1 s";
  if (durationMs < 10_000) return `${compactNumberFormatter.format(durationMs / 1_000)} s`;
  const seconds = Math.round(durationMs / 1_000);
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${minutes} min ${remainingSeconds.toString().padStart(2, "0")} s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours} h ${remainingMinutes.toString().padStart(2, "0")} min`;
}

function formatBytes(value: number): string {
  if (value < 1024) return `${value} o`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} Ko`;
  return `${(value / (1024 * 1024)).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} Mo`;
}

function canPreviewAttachmentImage(attachment: AttachmentRecord): boolean {
  return attachment.kind === "image" && ["image/png", "image/jpeg", "image/webp", "image/gif", "image/avif"].includes(attachment.mimeType);
}

type TimelineItem =
  | { kind: "message"; message: MessageRecord; timestamp: number; order: number }
  | { kind: "memory"; event: EventRecord; timestamp: number; order: number };

function buildTimeline(messages: MessageRecord[], events: EventRecord[]): TimelineItem[] {
  const items: TimelineItem[] = messages.map((message, index) => ({
    kind: "message",
    message,
    timestamp: new Date(message.createdAt).getTime(),
    order: index,
  }));
  for (const event of events) {
    if (!["memory.created", "memory.updated", "memory.forgotten"].includes(event.type)) continue;
    items.push({
      kind: "memory",
      event,
      timestamp: new Date(event.createdAt).getTime(),
      order: messages.length + event.seq,
    });
  }
  return items.sort((left, right) => left.timestamp - right.timestamp || left.order - right.order);
}

interface TimelineRange {
  start: number;
  end: number;
}

function latestTimelineRange(length: number, size = TIMELINE_BATCH_SIZE): TimelineRange {
  return { start: Math.max(0, length - Math.max(0, size)), end: length };
}

function sameTimelineRange(left: TimelineRange, right: TimelineRange): boolean {
  return left.start === right.start && left.end === right.end;
}
